import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, findCategory } from "@/data/menu";

const TILES = [
  { id: "cake-pops", cls: "lg:col-span-7 lg:row-span-2", h: "h-[420px] lg:h-auto" },
  { id: "strawberries", cls: "lg:col-span-5", h: "h-[300px] lg:h-auto" },
  { id: "cupcakes", cls: "lg:col-span-5", h: "h-[300px] lg:h-auto" },
  { id: "mini-donuts", cls: "sm:col-span-1 lg:col-span-4", h: "h-[300px] lg:h-auto" },
  { id: "gourmet-cookies", cls: "sm:col-span-1 lg:col-span-4", h: "h-[300px] lg:h-auto" },
];
const featured = new Set(TILES.map((t) => t.id));

const reveal = (i) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
});

const Tile = ({ tile, i, onPick }) => {
  const cat = findCategory(tile.id);
  const count = cat.items.length || "Build-your-own";
  return (
    <motion.button
      {...reveal(i)}
      onClick={() => onPick(cat.id)}
      data-testid={`bento-tile-${cat.id}`}
      className={`group relative overflow-hidden rounded-[2rem] text-left sm:col-span-2 ${tile.cls} ${tile.h}`}
    >
      <img src={cat.image} alt={cat.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#2D1E18]/85 via-[#2D1E18]/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 sm:p-8">
        <div>
          <p className="f-mono mb-2 text-xs uppercase tracking-[0.2em] text-[#FCE8E6]">{cat.tags.join(" • ")}</p>
          <h3 className="f-display text-3xl font-semibold leading-none text-white sm:text-4xl">{cat.name}</h3>
          <p className="mt-2 text-sm text-[#FCE8E6]">{typeof count === "number" ? `${count} flavors` : count}</p>
        </div>
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-cacao transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-[#E11D48] group-hover:text-white">
          <ArrowUpRight className="h-5 w-5" />
        </span>
      </div>
    </motion.button>
  );
};

const MoreTile = ({ onPick }) => (
  <motion.div {...reveal(5)} className="relative overflow-hidden rounded-[2rem] bg-[#E11D48] p-7 text-white sm:col-span-2 lg:col-span-4" data-testid="bento-more-tile">
    <p className="f-mono text-xs uppercase tracking-[0.2em] text-white/85">+ {CATEGORIES.length - featured.size} more collections</p>
    <div className="mt-4 flex flex-wrap gap-2">
      {CATEGORIES.filter((c) => !featured.has(c.id)).map((c) => (
        <button
          key={c.id}
          onClick={() => onPick(c.id)}
          data-testid={`bento-more-${c.id}`}
          className="rounded-full border border-white/40 px-3 py-1.5 text-xs font-medium transition-colors duration-300 hover:bg-white hover:text-[#E11D48]"
        >
          {c.name}
        </button>
      ))}
    </div>
  </motion.div>
);

export const Bento = ({ onPick }) => (
  <section id="collections" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-36" data-testid="collections-section">
    <div className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
      <motion.h2 {...reveal(0)} className="f-display text-5xl font-semibold leading-[0.95] text-cacao sm:text-6xl lg:col-span-7">
        The <span className="italic text-rose">signature</span> collections
      </motion.h2>
      <motion.p {...reveal(1)} className="max-w-md text-base leading-relaxed text-cacao-2 lg:col-span-5 lg:justify-self-end">
        Tap a collection to jump straight into its flavors. Mix and match as many as you like in one box.
      </motion.p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:auto-rows-[260px] lg:grid-cols-12 lg:gap-5">
      {TILES.map((t, i) => (
        <Tile key={t.id} tile={t} i={i} onPick={onPick} />
      ))}
      <MoreTile onPick={onPick} />
    </div>
  </section>
);
